import React, { useState } from 'react';
import SegmentedControl from './SegmentedControl';
import SwitchRow from './SwitchRow';
import { choice } from './choiceStyles';
import { TRUCK_CATEGORIES, categoryAxles, getTruckSpec, truckLabel, supportsTrailer, formatNo, isCraneCategory, BAG_TONS } from '../../../utils/truckCatalog';
import { calcLoads, loadsText, truckWarning, truckPayload, splitLoad, rebalanceSplit, bagsOnTruck } from '../../../utils/truckSuggest';
import { craneLiftWarning } from '../../../utils/craneLift';
import { COLORS } from '../../../styles/tokens';

// Bilvalg: kategori, antall aksler og henger. Tippi foreslår (suggested), bestiller kan endre.
// value/suggested: { category, axles, trailer? }. Henger-bryteren vises bare når mengden ikke går på bilen alene
// (eller når henger allerede er valgt). I fulle lass er mengden bilens egen nyttelast, så bryteren vises alltid (fullLoads).
export default function TruckPicker({ value, suggested, onChange, overrides, unit, quantityTons, split, onSplitChange, splitProblem, craneLift, onCraneLiftChange, fullLoads = false }) {
  const [editingSplit, setEditingSplit] = useState(false);
  // Feltene blir stående åpne mens bestiller skriver, også om begge tømmes.
  const [liftOpen, setLiftOpen] = useState(() => !!(craneLift?.tons?.trim() || craneLift?.meters?.trim()));
  const axleOptions = categoryAxles(value.category);
  const spec = getTruckSpec(value.category, value.axles, overrides);
  const trailerSpec = getTruckSpec('trailer', null, overrides);
  const canTrailer = supportsTrailer(value.category) && unit !== 'bags';
  const trailerOn = value.trailer === true && canTrailer;
  const payload = truckPayload({ ...value, trailer: trailerOn }, overrides);
  const lassTons = unit === 'bags' ? Number(quantityTons) * BAG_TONS : quantityTons;
  const loadInfo = (unit === 'tons' || unit === 'bags') && payload ? calcLoads(lassTons, payload) : null;
  const loadLine = loadsText(loadInfo, unit);
  const truckAlone = unit === 'tons' && spec ? calcLoads(quantityTons, spec.payloadTons) : null;
  const showTrailer = canTrailer && (trailerOn || fullLoads || (truckAlone != null && truckAlone.loads > 1));
  // Hint når henger gir færre lass enn bilen alene (Tippi foreslår ikke henger selv).
  const withTrailer = canTrailer && !trailerOn && unit === 'tons' && loadInfo
    ? calcLoads(quantityTons, truckPayload({ ...value, trailer: true }, overrides)) : null;
  const trailerHint = withTrailer && withTrailer.loads < loadInfo.loads
    ? (loadsText(withTrailer) ? `Med henger blir det ${loadsText(withTrailer)}.` : 'Med henger tar bilen alt på ett lass.') : null;
  const trailerSub = !trailerOn && trailerHint ? trailerHint : `Gir ${formatNo(trailerSpec.payloadTons)} t ekstra nyttelast`;
  const warning = truckWarning({ category: value.category, unit });
  const isCrane = isCraneCategory(value.category);
  const liftFilled = !!(craneLift?.tons?.trim() || craneLift?.meters?.trim());
  const liftShown = isCrane && (liftOpen || liftFilled);
  const liftWarning = isCrane && craneLift ? craneLiftWarning(craneLift, spec?.craneTm) : null;
  const showSplit = trailerOn && loadInfo != null;
  const auto = showSplit ? splitLoad(loadInfo.tonsPerLoad, spec.payloadTons, trailerSpec.payloadTons) : null;
  const shown = split ?? (auto ? { truck: formatNo(auto.truck), trailer: formatNo(auto.trailer) } : { truck: '', trailer: '' });
  const splitOpen = editingSplit || !!splitProblem;
  const suggestedLabel = TRUCK_CATEGORIES.find(c => c.value === suggested?.category)?.label;

  // trailer-nøkkelen finnes bare som true, og bare for kategorier som kan ha henger.
  const emit = (next, trailer) => onChange(trailer && supportsTrailer(next.category) ? { ...next, trailer: true } : next);

  function pickCategory(category) {
    const options = categoryAxles(category);
    const axles = options.length === 0 ? null : options.includes(value.axles) ? value.axles : (options.includes(3) ? 3 : options[0]);
    emit({ category, axles }, value.trailer === true);
  }

  return (
    <div style={choice.card}>
      <div style={choice.title}>Bil</div>

      <span style={choice.label}>Biltype</span>
      <div role="group" aria-label="Bilkategori" style={choice.pills}>
        {TRUCK_CATEGORIES.map(c => {
          const on = value.category === c.value;
          return (
            <button key={c.value} type="button" aria-pressed={on} onClick={() => pickCategory(c.value)}
                    style={{ ...choice.pill, ...(on ? choice.pillOn : {}) }}>
              {c.label}
            </button>
          );
        })}
      </div>
      {suggestedLabel && <div style={choice.hint}>Tippi foreslår {suggestedLabel.toLowerCase()}</div>}

      {axleOptions.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <span style={choice.label}>Antall aksler</span>
          <SegmentedControl
            ariaLabel="Antall aksler" fill value={value.axles}
            options={axleOptions.map(a => ({ value: a, label: String(a) }))}
            onChange={a => emit({ category: value.category, axles: a }, value.trailer === true)}
          />
        </div>
      )}

      {showTrailer && (
        <>
          <div style={choice.divider} />
          <SwitchRow
            title="Med henger" sub={trailerSub} checked={trailerOn}
            onChange={on => emit({ category: value.category, axles: value.axles }, on)}
          />
        </>
      )}

      {spec && payload != null && (
        <>
          <div style={choice.divider} />
          <p style={choice.sum}>{truckLabel(value.category, value.axles, trailerOn)} · nyttelast ca. {formatNo(payload)} t</p>
          <div style={choice.hint}>{spec.description} Bredde {formatNo(spec.maxWidthM)} m · Høyde {formatNo(spec.maxHeightM)} m</div>
        </>
      )}

      {isCrane && spec?.craneTm && <div style={choice.hint}>Kran: ca. {formatNo(spec.craneTm)} tonnmeter</div>}

      {isCrane && (
        <div style={{ marginTop: 8 }}>
          {!liftFilled && (
            <button type="button" style={choice.link} onClick={() => setLiftOpen(v => !v)}>
              {liftOpen ? 'Skjul' : 'Oppgi tyngste løft'}
            </button>
          )}
          {liftShown && (
            <div style={styles.split}>
              <label style={styles.splitField}>
                <span style={choice.label}>Tyngste løft (tonn)</span>
                <input inputMode="decimal" value={craneLift?.tons ?? ''} style={styles.splitInput}
                       onChange={e => { setLiftOpen(true); onCraneLiftChange?.({ tons: e.target.value, meters: craneLift?.meters ?? '' }); }} />
              </label>
              <label style={styles.splitField}>
                <span style={choice.label}>Avstand fra bilen (meter)</span>
                <input inputMode="decimal" value={craneLift?.meters ?? ''} style={styles.splitInput}
                       onChange={e => { setLiftOpen(true); onCraneLiftChange?.({ tons: craneLift?.tons ?? '', meters: e.target.value }); }} />
              </label>
            </div>
          )}
        </div>
      )}

      {unit === 'bags' && (
        <div style={{ background: COLORS.surfaceHover, borderRadius: 12, padding: '10px 12px', marginTop: 10 }}>
          <div style={choice.hint}>Storsekk veier ca. {formatNo(BAG_TONS)} t.</div>
          <div style={choice.hint}>Krever kranbil.</div>
          {payload != null && <div style={choice.hint}>{truckLabel(value.category, value.axles, false)} tar ca. {bagsOnTruck(payload)} sekker.</div>}
        </div>
      )}

      {loadLine && <div style={{ ...choice.sum, marginTop: 8 }}>Mengden blir {loadLine} med denne bilen.</div>}

      {showSplit && (
        <div style={{ marginTop: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
            <span style={{ ...choice.hint, margin: 0 }}>Fordeling: bil {shown.truck} t · henger {shown.trailer} t</span>
            {!splitProblem && <button type="button" style={choice.link} onClick={() => setEditingSplit(v => !v)}>{editingSplit ? 'Ferdig' : 'Endre'}</button>}
          </div>
          {splitOpen && (
            <div style={styles.split}>
              <label style={styles.splitField}>
                <span style={choice.label}>Bil (tonn)</span>
                <input inputMode="decimal" value={shown.truck} style={styles.splitInput}
                       onChange={e => onSplitChange?.(rebalanceSplit('truck', e.target.value, loadInfo.tonsPerLoad))} />
              </label>
              <label style={styles.splitField}>
                <span style={choice.label}>Henger (tonn)</span>
                <input inputMode="decimal" value={shown.trailer} style={styles.splitInput}
                       onChange={e => onSplitChange?.(rebalanceSplit('trailer', e.target.value, loadInfo.tonsPerLoad))} />
              </label>
            </div>
          )}
        </div>
      )}
      {splitProblem && <div role="alert" style={choice.warning}>{splitProblem}</div>}
      {warning && <div role="alert" style={choice.warning}>{warning}</div>}
      {liftWarning && <div role="alert" style={choice.warning}>{liftWarning}</div>}
    </div>
  );
}

const styles = {
  split:      { display: 'flex', gap: 10, marginTop: 8 },
  splitField: { flex: 1, display: 'flex', flexDirection: 'column' },
  splitInput: { height: 44, padding: '0 12px', borderRadius: 12, border: `1px solid ${COLORS.border}`, fontSize: 15, fontFamily: 'inherit', background: COLORS.white, color: COLORS.textDark, width: '100%', boxSizing: 'border-box' },
};
